'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { rehydrateProjectUiMode, useProjectUiModeStore } from '@/utils/projectUiMode';

type Mode = 'normal' | 'experienced' | 'pro';

const MODES: { id: Mode; label: string; short: string; desc: string; color: string }[] = [
  { id: 'normal', label: 'Презентация', short: '👤', desc: 'Описание и материалы проекта', color: 'bg-site-action/20' },
  { id: 'experienced', label: 'Панель', short: '⚡', desc: 'Разделы и рабочие инструменты', color: 'bg-site-success/20' },
  { id: 'pro', label: 'Граф', short: '🔥', desc: 'Объекты и связи проекта', color: 'bg-site-warning/20' },
];

export default function ProjectModeSwitch({ className, inMenu = false }: { className?: string; inMenu?: boolean }) {
  const mode = useProjectUiModeStore((s) => s.mode);
  const setMode = useProjectUiModeStore((s) => s.setMode);
  const syncFromServer = useProjectUiModeStore((s) => s.syncFromServer);
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    rehydrateProjectUiMode();
    setMounted(true);
    void syncFromServer();
  }, [syncFromServer]);

  // Закрытие popup при клике вне
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!mounted) return null;

  const idx = mode === 'normal' ? 0 : mode === 'experienced' ? 1 : 2;
  const currentMode = MODES[idx];
  const activeBg = currentMode.color;
  const hlTranslate = idx === 0 ? 'translateX(0)' : idx === 1 ? 'translateX(100%)' : 'translateX(200%)';

  const isProjectsList = pathname === '/projects';
  const isInteractive = !isProjectsList;

  const handleSelect = (m: Mode) => {
    setMode(m);
    setOpen(false);
  };

  return (
    <>
      {/* Мобильная версия — компактная кнопка + popup */}
      <div className={`${inMenu ? 'hidden' : 'lg:hidden'} relative ${className || ''}`} ref={popupRef}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          disabled={!isInteractive}
          className={`flex items-center gap-1.5 h-9 px-3 rounded-full border border-site-border bg-site-bg/50 backdrop-blur-sm text-sm font-medium transition-all ${
            isInteractive ? 'hover:bg-site-surface/60 active:scale-95' : 'opacity-50 cursor-not-allowed'
          }`}
          aria-label="Выбрать представление проекта"
          aria-expanded={open}
          title={`${currentMode.label} — ${currentMode.desc}`}
        >
          <span>{currentMode.short}</span>
          <span className="text-site-text">{currentMode.label}</span>
          <span className={`text-xs transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {/* Popup меню */}
        {open && (
          <div className="absolute top-full right-0 mt-2 w-48 rounded-xl border border-site-border bg-site-bg/95 backdrop-blur-xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
            {MODES.map((m, i) => (
              <button
                key={m.id}
                type="button"
                onClick={() => handleSelect(m.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  mode === m.id
                    ? 'bg-site-surface/80 text-site-text'
                    : 'text-site-secondary hover:bg-site-surface/50 hover:text-site-text'
                } ${i !== MODES.length - 1 ? 'border-b border-site-border' : ''}`}
              >
                <span className="text-lg">{m.short}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{m.label}</div>
                  <div className="text-xs text-site-muted truncate">{m.desc}</div>
                </div>
                {mode === m.id && <span className="text-site-success text-sm">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Десктопная версия — горизонтальный переключатель */}
      <div
        className={`pm-switch relative ${inMenu ? 'inline-flex w-full' : 'hidden lg:inline-flex w-[260px]'} h-10 select-none items-center rounded-full border-2 border-site-border bg-site-bg/50 backdrop-blur-sm p-0.5 ${
          className || ''
        } ${isInteractive ? '' : 'opacity-50 pointer-events-none cursor-not-allowed'}`}
        role="group"
        aria-label="Представление проекта"
        aria-disabled={!isInteractive}
        title={`${currentMode.label} (${currentMode.desc})`}
      >
        {/* Обводка активного сегмента с бегущей точкой */}
        <span
          aria-hidden
          className="pm-hl absolute top-0.5 bottom-0.5 left-0.5 rounded-full border border-site-warning/70 shadow-[0_10px_26px_rgba(0,0,0,0.30)] transition-transform duration-200 pointer-events-none"
          style={{ width: 'calc(33.333% - 2px)', transform: hlTranslate }}
        >
          <svg className="pm-dot-svg" viewBox="0 0 80 32" preserveAspectRatio="none">
            <defs>
              <path
                id="pillPath"
                d="M16,1 L64,1 A15,15 0 0 1 64,31 L16,31 A15,15 0 0 1 16,1"
                fill="none"
              />
              <radialGradient id="dotGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="rgba(255,220,180,1)" />
                <stop offset="60%" stopColor="rgba(251,146,60,0.6)" />
                <stop offset="100%" stopColor="rgba(251,146,60,0)" />
              </radialGradient>
            </defs>
            <circle className="pm-dot" r="4" fill="url(#dotGlow)">
              <animateMotion dur="20s" repeatCount="indefinite">
                <mpath href="#pillPath" />
              </animateMotion>
            </circle>
          </svg>
        </span>

        {/* Заливка активного сегмента */}
        <span
          aria-hidden
          className={`absolute top-0.5 bottom-0.5 left-0.5 rounded-full shadow-sm transition-transform duration-200 pointer-events-none ${activeBg}`}
          style={{ width: 'calc(33.333% - 2px)', transform: hlTranslate }}
        />

        {/* Кнопки */}
        {MODES.map((m, i) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            className={`relative z-10 flex-1 h-full flex items-center justify-center rounded-full text-xs font-semibold tracking-tight transition-colors ${
              idx === i ? 'text-site-text' : 'text-site-secondary hover:text-site-text'
            }`}
            aria-pressed={idx === i}
          >
            {m.label}
          </button>
        ))}

        <style jsx>{`
          .pm-switch {
          }
          .pm-hl {
            overflow: visible;
          }
          .pm-dot-svg {
            position: absolute;
            inset: -3px;
            width: calc(100% + 6px);
            height: calc(100% + 6px);
            overflow: visible;
            pointer-events: none;
          }
          .pm-dot {
            filter: blur(0.5px);
          }
        `}</style>
      </div>
    </>
  );
}
